import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Pencil, Plus } from 'lucide-react';
import { useCalories } from '../stores/calories.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Gauge, gaugeTone, TONE_TEXT, type GaugeTone } from '../ui/Gauge.js';
import { EmptyState } from '../components/EmptyState.js';
import { ListSkeleton } from '../components/TaskList.js';
import { Field } from '../ui/Field.js';
import { Tabs } from '../ui/Tabs.js';
import { FoodLogModal } from '../components/FoodLogModal.js';
import { FoodEditModal } from '../components/FoodEditModal.js';
import { addDays, formatFR, formatLong, formatTimeParis, todayInParis } from '../lib/dates.js';
import { PageHeader } from '../components/PageHeader.js';
import { cn } from '../lib/cn.js';
import type { DaySummary, Food, FoodLog, WeekDayPoint } from '../api/food.js';

type Tab = 'day' | 'foods';

/**
 * The number the eye lands on. "560 restantes" helps you choose lunch;
 * "69 %" helps with nothing. A fresh day shows the budget, never "0 %" —
 * at 8 a.m. zero reads as failure when it is simply the neutral state.
 */
function dominant(
  day: DaySummary,
  target: number
): { value: string; label: string; tone: GaugeTone } {
  const tone = gaugeTone(day.kcal, target);
  if (day.kcal === 0) {
    return { value: String(target), label: 'disponibles', tone: 'success' };
  }
  const remaining = day.kcalRemaining ?? 0;
  if (remaining < 0) {
    return { value: `−${Math.abs(remaining)}`, label: 'de dépassement', tone };
  }
  return { value: String(remaining), label: 'restantes', tone };
}

/**
 * No target for the day on screen. Two different situations share this shape:
 * the account has no goal yet (onboarding — offer to define one), or a goal
 * exists but starts after this day (a past day — nothing to define, the total
 * is simply shown untargeted).
 *
 * Never rendered while `hasGoal` is null: neither wording is true yet, and
 * guessing flashes the wrong one — the CTA on a past day is exactly the bug this
 * component was fixed for.
 */
function NoGoal({
  kcal,
  isToday,
  hasGoal,
  onDefine,
}: {
  kcal: number;
  isToday: boolean;
  hasGoal: boolean | null;
  onDefine: () => void;
}): React.ReactElement {
  const onboarding = hasGoal === false;
  return (
    <div className="pile pile-serree">
      {kcal > 0 && (
        <div>
          {/* Neutral, not green: with no target there is nothing to be on track for. */}
          <p aria-hidden="true" className="chiffre chiffre-hero">
            {kcal}
          </p>
          <p className="discret mt-1">kcal {isToday ? 'aujourd’hui' : 'ce jour-là'}</p>
        </div>
      )}
      {onboarding ? (
        <>
          <p className="discret">Définis un objectif pour voir ta progression.</p>
          <Button className="self-start" onClick={onDefine}>
            Définir un objectif
          </Button>
        </>
      ) : (
        <p className="discret">
          Aucun objectif en vigueur {isToday ? 'aujourd’hui' : 'ce jour-là'}.
        </p>
      )}
    </div>
  );
}

// `.colonnes` du kit : une jauge debout par jour de la semaine.
function WeekStrip({
  days,
  today,
}: {
  days: WeekDayPoint[];
  today: string;
}): React.ReactElement {
  const peak = Math.max(...days.map((d) => Math.max(d.kcal, d.target ?? 0)), 1);
  const target = days.find((d) => d.target !== null)?.target ?? null;
  return (
    <ol
      className={cn('colonnes', target !== null && 'colonnes-objectif')}
      style={
        {
          '--colonnes-hauteur': '4.5rem',
          ...(target !== null && { '--objectif': Math.round((target / peak) * 100) }),
        } as React.CSSProperties
      }
      aria-label={target !== null ? `Calories de la semaine, objectif ${target} kcal par jour` : 'Calories de la semaine'}
    >
      {days.map((d) => {
        const height = d.kcal === 0 ? 0 : Math.max(6, Math.round((d.kcal / peak) * 100));
        const over = d.target !== null && d.kcal > d.target;
        return (
          <li
            key={d.date}
            aria-label={`${formatFR(d.date)}, ${d.kcal}${d.target !== null ? ` sur ${d.target}` : ''} kcal`}
            aria-current={d.date === today ? 'date' : undefined}
            className={cn('colonne', over ? 'colonne-erreur' : 'colonne-succes')}
            style={{ '--valeur': height } as React.CSSProperties}
          />
        );
      })}
    </ol>
  );
}

function GoalEditor({ onDone }: { onDone: () => void }): React.ReactElement {
  const summary = useCalories((s) => s.summary);
  const setGoal = useCalories((s) => s.setGoal);
  const [kcal, setKcal] = useState(String(summary?.day.kcalTarget ?? ''));
  const [protein, setProtein] = useState(String(summary?.day.proteinTarget ?? ''));
  const [saving, setSaving] = useState(false);

  async function save(): Promise<void> {
    const k = Number(kcal);
    if (!Number.isFinite(k) || k <= 0) return;
    setSaving(true);
    try {
      const p = Number(protein);
      await setGoal({
        kcalTarget: Math.round(k),
        proteinTarget: Number.isFinite(p) && protein.trim() !== '' ? Math.round(p) : null,
      });
      onDone();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rangee items-end">
      <Field label="kcal / jour" className="w-28">
        <Input
          value={kcal}
          onChange={(e) => setKcal(e.target.value)}
          inputMode="numeric"
          autoFocus
        />
      </Field>
      <Field label="Protéines / jour" className="w-32">
        <Input value={protein} onChange={(e) => setProtein(e.target.value)} inputMode="numeric" />
      </Field>
      <Button onClick={() => void save()} loading={saving}>
        Enregistrer
      </Button>
      <Button variant="ghost" onClick={onDone}>
        Annuler
      </Button>
    </div>
  );
}

// La trace de l'anneau : la journée EN COURS, puis l'avertissement et le dépassement.
const ANNEAU_TEINTE: Record<GaugeTone, string> = {
  success: 'var(--encre-courant)',
  warning: 'var(--encre-attention)',
  danger: 'var(--encre-erreur)',
};

const TABS = [
  { id: 'day', label: 'Jour' },
  { id: 'foods', label: 'Aliments' },
] as const;

export function Calories(): React.ReactElement {
  const date = useCalories((s) => s.date);
  const summary = useCalories((s) => s.summary);
  const entries = useCalories((s) => s.entries);
  const foods = useCalories((s) => s.foods);
  const loading = useCalories((s) => s.loading);
  const error = useCalories((s) => s.error);
  const hasGoal = useCalories((s) => s.hasGoal);
  const loadDay = useCalories((s) => s.loadDay);
  const loadGoal = useCalories((s) => s.loadGoal);
  const loadFoods = useCalories((s) => s.loadFoods);

  const [tab, setTab] = useState<Tab>('day');
  const [logOpen, setLogOpen] = useState(false);
  const [editing, setEditing] = useState<FoodLog | null>(null);
  const [foodModal, setFoodModal] = useState<{ open: boolean; food: Food | null }>({
    open: false,
    food: null,
  });
  const [editingGoal, setEditingGoal] = useState(false);

  const today = todayInParis();

  useEffect(() => {
    void loadDay();
    void loadGoal();
  }, [loadDay, loadGoal]);

  useEffect(() => {
    if (tab === 'foods') void loadFoods();
  }, [tab, loadFoods]);

  const sorted = useMemo(
    () => [...entries].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [entries]
  );

  const head =
    summary && summary.day.kcalTarget !== null
      ? dominant(summary.day, summary.day.kcalTarget)
      : null;

  return (
    <div // pb-28: the floating + button is fixed, so the list needs room to scroll
      // clear of it — otherwise the last entry’s calories sit under it for good.
      className="max-w-2xl mx-auto w-full px-4 md:px-6 pt-4 md:pt-6 pb-28 space-y-4">
      {/* Pas d'ActionButtons ici : la page a déjà son + flottant, deux boutons d'ajout
          concurrents pour deux choses différentes (ADR 0007). */}
      <PageHeader title="Calories" surtitre={formatLong(date, 'fr')} />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Vue des calories" />

      <p className="alerte alerte-erreur" role="alert">
        {error}
      </p>

      {tab === 'day' ? (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Button
                variant="quiet"
                size="icon"
                onClick={() => void loadDay(addDays(date, -1))}
                aria-label="Jour précédent"
              >
                <ChevronLeft size={16} aria-hidden />
              </Button>
              <span className="chiffre text-sm text-muted">
                {date === today ? 'Aujourd’hui' : formatFR(date)}
              </span>
              <Button
                variant="quiet"
                size="icon"
                onClick={() => void loadDay(addDays(date, 1))}
                disabled={date >= today}
                aria-label="Jour suivant"
              >
                <ChevronRight size={16} aria-hidden />
              </Button>
            </div>
            <Button
              variant="quiet"
              size="icon"
              onClick={() => setEditingGoal((v) => !v)}
              aria-pressed={editingGoal}
              aria-label="Modifier l’objectif"
            >
              <Pencil size={16} aria-hidden />
            </Button>
          </div>

          {/* Nothing loaded and nothing loading: render no card at all rather than an
              empty bordered box next to the error banner. */}
          {(editingGoal || loading || summary) && (
            // La pile est DANS la carte : `.carte` pose son propre `display`.
            <section className="carte carte-serree">
              <div className="pile">
              {editingGoal ? (
                <GoalEditor onDone={() => setEditingGoal(false)} />
              ) : (loading && !summary) ||
                (summary !== null && summary.day.kcalTarget === null && hasGoal === null) ? (
                <div className="pile pile-serree" aria-busy="true">
                  <div className="squelette h-14 w-32" />
                  <div className="squelette h-2" />
                </div>
              ) : summary && summary.day.kcalTarget === null ? (
                <NoGoal
                  kcal={summary.day.kcal}
                  isToday={date === today}
                  hasGoal={hasGoal}
                  onDefine={() => setEditingGoal(true)}
                />
              ) : summary && head ? (
                <>
                  {/* L'anneau dit où on en est, le chiffre ce qu'il reste (Braise 2). Le chiffre
                      ne se colore plus que pour avertir : un vert géant « tout va bien » prenait
                      la place de la marque sur la page la plus regardée. */}
                  <div className="flex items-center gap-5">
                    <span
                      className="anneau-cadre"
                      style={
                        {
                          '--valeur': Math.min(100, Math.round((summary.day.kcal / summary.day.kcalTarget!) * 100)),
                          '--anneau-taille': '5.5rem',
                          '--anneau-epaisseur': 3.5,
                          '--anneau-teinte': ANNEAU_TEINTE[head.tone],
                        } as React.CSSProperties
                      }
                    >
                      <svg className="anneau" viewBox="0 0 36 36" role="img" aria-label={`${summary.day.kcal} sur ${summary.day.kcalTarget} calories`}>
                        <circle className="anneau-piste" cx="18" cy="18" r="16" pathLength="100" />
                        <circle className="anneau-trace" cx="18" cy="18" r="16" pathLength="100" />
                      </svg>
                      <span className="anneau-valeur">
                        {Math.round((summary.day.kcal / summary.day.kcalTarget!) * 100)}%
                      </span>
                    </span>
                    <div className="min-w-0">
                      <p aria-hidden="true" className={cn('chiffre chiffre-hero', head.tone !== 'success' && TONE_TEXT[head.tone])}>
                        {head.value}
                      </p>
                      <p className="discret mt-1">
                        {head.label} · <span className="chiffre">{summary.day.kcal} / {summary.day.kcalTarget}</span>
                      </p>
                    </div>
                  </div>

                  <div className="pile pile-serree">
                    <div className="flex items-baseline justify-between">
                      <span className="discret">Protéines</span>
                      <span className="ligne-repere">
                        {summary.day.protein}
                        {summary.day.proteinTarget !== null && ` / ${summary.day.proteinTarget}`} g
                      </span>
                    </div>
                    <Gauge
                      value={summary.day.protein}
                      target={summary.day.proteinTarget}
                      tone="success"
                      label={`${summary.day.protein} sur ${summary.day.proteinTarget ?? '—'} grammes de protéines`}
                    />
                  </div>

                  <div className="pile pile-serree">
                    <div className="flex items-baseline justify-between">
                      <span className="discret">Semaine</span>
                      <span className="ligne-repere">
                        {summary.week.remaining === null
                          ? '—'
                          : summary.week.remaining >= 0
                            ? `reste ${summary.week.remaining} kcal`
                            : `${summary.week.remaining} kcal`}
                      </span>
                    </div>
                    <WeekStrip days={summary.week.days} today={date} />
                  </div>
                </>
              ) : null}
              </div>
            </section>
          )}

          {loading && sorted.length === 0 ? (
            <ListSkeleton />
          ) : sorted.length === 0 ? (
            <EmptyState>
              {date === today ? 'Rien de logué aujourd’hui.' : 'Aucune entrée ce jour-là.'}
            </EmptyState>
          ) : (
            <ul className="liste">
              {sorted.map((e) => (
                <li key={e.logId}>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(e);
                      setLogOpen(true);
                    }}
                    aria-label={`${e.label}, ${e.kcal} kcal, à ${formatTimeParis(e.createdAt)}`}
                    className="ligne ligne-cliquable"
                  >
                    <span className="ligne-repere w-11">{formatTimeParis(e.createdAt)}</span>
                    <span className="ligne-corps">
                      <span className="ligne-titre">
                        {e.label}
                        {e.grams !== null && <span className="text-muted"> · {e.grams} g</span>}
                      </span>
                    </span>
                    <span className="ligne-repere">{e.kcal} kcal</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          <div className="flex justify-end">
            <Button onClick={() => setFoodModal({ open: true, food: null })}>
              <Plus size={16} aria-hidden /> Aliment
            </Button>
          </div>
          {foods.length === 0 ? (
            <EmptyState>Ton catalogue se remplira tout seul au fil des logs.</EmptyState>
          ) : (
            <ul className="liste">
              {foods.map((f) => (
                <li key={f.foodId}>
                  <button
                    type="button"
                    onClick={() => setFoodModal({ open: true, food: f })}
                    aria-label={`Modifier ${f.name}`}
                    className="ligne ligne-cliquable"
                  >
                    <span className="ligne-corps">
                      <span className="ligne-titre">{f.name}</span>
                    </span>
                    <span className="ligne-repere">
                      {f.kcalPer100g !== null && `${f.kcalPer100g}/100 g`}
                      {f.kcalPer100g !== null && f.kcalPerPortion !== null && ' · '}
                      {f.kcalPerPortion !== null &&
                        `${f.kcalPerPortion}/${f.portionLabel ?? 'portion'}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {tab === 'day' && (
        <Button
          className="bouton-flottant"
          onClick={() => {
            setEditing(null);
            setLogOpen(true);
          }}
          aria-label="Ajouter un aliment"
        >
          <Plus size={24} aria-hidden />
        </Button>
      )}

      <FoodLogModal
        open={logOpen}
        onClose={() => setLogOpen(false)}
        date={date}
        entry={editing}
      />
      <FoodEditModal
        open={foodModal.open}
        onClose={() => setFoodModal({ open: false, food: null })}
        food={foodModal.food}
      />
    </div>
  );
}
